import { addDays, differenceInDays, format, isBefore, isValid, parse as parseDfns, startOfDay } from 'date-fns';
import groupsConfig from '@/config/groups';
import searchConfig from '@/config/search';

/**
 * Build search location label fields from a sanitised query (output of parseSearchQuery).
 *
 * @param {{ q?: string|null, type?: string|null, id?: number|null }} sanitizedQuery
 * @returns {{ label: string, labelSmall: string, type: string|null, id: number|null, value: string }} Location fields for search UI and store.
 */
export function getSearchLocationFromSanitizedQuery(sanitizedQuery) {
  const q = sanitizedQuery?.q || '';
  const [city, ...rest] = q.split(',');
  return {
    label: city?.trim() || '',
    labelSmall: rest.join(',').trim() || '',
    type: sanitizedQuery?.type ?? null,
    id: sanitizedQuery?.id ?? null,
    value: String(sanitizedQuery?.id ?? ''),
  };
}

/**
 * Build inventory location (HW search API shape) when the properties API
 * did not return location data. Uses query params from the search form plus city id.
 *
 * @param {Record<string, string | string[] | undefined>} query - route.query
 * @param {number | null | undefined} cityId - parsed search id (city)
 * @returns {{ city: { id: number|null, name: string, idCountry: null, country: string }, region: null }} Inventory location fallback built from the route query.
 */
export function buildSearchLocationFromRouteQuery(query, cityId) {
  const rawQ = query?.q;
  const q = Array.isArray(rawQ) ? rawQ[0] : rawQ;
  const qStr = q != null && q !== '' ? String(q) : '';

  const rawCity = query?.city;
  const rawCountry = query?.country;
  let cityName = (Array.isArray(rawCity) ? rawCity[0] : rawCity)?.trim?.() || '';
  let countryName = (Array.isArray(rawCountry) ? rawCountry[0] : rawCountry)?.trim?.() || '';

  if (!cityName && qStr) {
    const parts = qStr.split(',');
    cityName = parts[0]?.trim() || '';
    countryName = parts.slice(1).join(',').trim() || countryName;
  }

  const id = cityId != null && !Number.isNaN(Number(cityId)) ? Number(cityId) : null;

  return {
    city: {
      id,
      name: cityName,
      idCountry: null,
      country: countryName,
    },
    region: null,
  };
}

export function useSearchParams() {
  /**
   * Returns the given date if it is a legal check-in date, or the default check-in.
   * Default check-in is tomorrow; it may be incremented by addDays when provided.
   *
   * @param {Date} [checkIn] - Parsed check-in date from query
   * @param {number} [addDaysCount] - Days to add to default check-in
   * @returns {Date} Coerced check-in date, or default (tomorrow + addDaysCount).
   */
  const getCheckinDate = (checkIn, addDaysCount = 0) => {
    const today = startOfDay(Date.now());
    const defaultCheckIn = addDaysCount ? addDays(today, Number.parseInt(addDaysCount, 10)) : addDays(today, 1);

    if (!isValid(checkIn) || isBefore(checkIn, addDays(today, -1))) {
      return defaultCheckIn;
    }

    return checkIn;
  };

  /**
   * Returns the given date if it is a legal check-out date given the check-in date, or a default.
   * Default check-out is check-in plus defaultNights; clamped by maxNights when extendSearch is true.
   *
   * @param {Date} checkIn
   * @param {Date} [checkOut]
   * @param {boolean} [extendSearch]
   * @returns {Date} Coerced check-out date, or default (checkIn + defaultNights).
   */
  const getCheckoutDate = (checkIn, checkOut, extendSearch = true) => {
    const defaultCheckOut = addDays(checkIn, searchConfig.defaultNights);

    if (!isValid(checkOut)) {
      return defaultCheckOut;
    }

    const nights = differenceInDays(checkOut, checkIn);
    const maxNights = extendSearch ? searchConfig.extendedMaxNights : searchConfig.maxNights;

    if (nights < 1) {
      return defaultCheckOut;
    }

    if (nights > maxNights) {
      return addDays(checkIn, maxNights);
    }

    return checkOut;
  };

  const validateGroupQuery = (query) => {
    if (!query?.guests || Number(query.guests) < groupsConfig.minGuests) {
      return true;
    }

    return (
      groupsConfig.groupTypes.some((e) => Number(e.value) === Number(query.groupType))
      && []
        .concat(query.groupAgeRange || [])
        .every((e) => groupsConfig.ageRanges.some((r) => Number(r.value) === Number(e)))
    );
  };

  const validateSearchQueryStrict = (query) => {
    return (
      query?.q != null && /^\d+/.test(String(query?.id ?? '')) && ['city', 'district', 'region'].includes(query?.type)
    );
  };

  /**
   * Parse and sanitise search params from the URL query.
   * From, to, and guests are coerced to legal values.
   *
   * @param {Record<string, string>} query - Route query object
   * @param {object} [options]
   * @param {boolean} [options.extendSearch]
   * @param {number} [options.addDays]
   * @param {boolean} [options.isMobile] - When true uses defaultGuests.mobile, else desktop
   * @returns {object} Sanitised search params with fromAsString, toAsString, guests, etc.
   */
  const parseSearchQuery = (query, options = {}) => {
    const {
      extendSearch = true,
      addDays: addDaysCount = 0,
      isMobile = false,
    } = options;

    const defaultGuests = isMobile ? searchConfig.defaultGuests.mobile : searchConfig.defaultGuests.desktop;

    const fromParsed = query?.from ? parseDfns(query.from, 'yyyy-MM-dd', new Date()) : null;
    const toParsed = query?.to ? parseDfns(query.to, 'yyyy-MM-dd', new Date()) : null;

    const from = getCheckinDate(fromParsed, addDaysCount);
    const to = getCheckoutDate(from, toParsed, extendSearch);

    const guestsRaw = Number(query?.guests) || defaultGuests;
    const guests = guestsRaw > 0 && guestsRaw <= searchConfig.maxGuests ? guestsRaw : defaultGuests;

    const groupAgeRangeRaw = [].concat(query?.groupAgeRange || []);
    const groupAgeRange = groupAgeRangeRaw.map((e) => Number(e));

    return {
      type: query?.type || null,
      id: Number.parseInt(query?.id, 10) || null,
      from,
      to,
      fromAsString: format(from, 'yyyy-MM-dd'),
      toAsString: format(to, 'yyyy-MM-dd'),
      q: query?.q || null,
      guests,
      groupType: query?.groupType != null ? Number(query.groupType) : null,
      groupAgeRange,
    };
  };

  /**
   * Validate that the query contains legal search and group values.
   * Parameters that will be coerced to legal values are not validated.
   *
   * @param {Record<string, string>} query
   * @returns {boolean} True if query has legal search and group values.
   */
  const validateSearchQuery = (query) => {
    return validateSearchQueryStrict(query) && validateGroupQuery(query);
  };

  return {
    parseSearchQuery,
    validateSearchQuery,
  };
}
